/**
 * Route normalization for RUM page views. The portal's routes are flat (no
 * path params — identifiers ride in the query string, see
 * `docs/observability/telemetry-inventory.md` §B1), so normalization is a
 * lookup on the first path segment plus a hard strip of query + hash. This is
 * the ONLY place a pathname is turned into telemetry; it emits a stable,
 * PHI-free template and never echoes an unknown raw path.
 */

export type FeatureArea =
  | 'patient_workspace'
  | 'patient_management'
  | 'clinician_management'
  | 'templates'
  | 'custom_activities'
  | 'emr'
  | 'extension'
  | 'settings'
  | 'privacy'
  | 'shell'
  | 'unknown';

export type ScreenType =
  | 'workspace'
  | 'list'
  | 'editor'
  | 'integration'
  | 'settings'
  | 'public'
  | 'loader'
  | 'unknown';

export interface NormalizedRoute {
  /** Stable, low-cardinality template. Never contains an identifier. */
  pageTemplate: string;
  featureArea: FeatureArea;
  screenType: ScreenType;
}

interface RouteEntry extends NormalizedRoute {
  /** First path segment (no leading slash); '' is the index route. */
  segment: string;
}

// Mirrors the <Route> table in App.tsx. `/patient` (singular) is the patient
// workspace whose query string carries patient-id/case-id/plan-id — its
// template is the canonical, param-shaped form so dashboards read naturally.
const ROUTE_TABLE: readonly RouteEntry[] = [
  { segment: '', pageTemplate: '/', featureArea: 'shell', screenType: 'loader' },
  {
    segment: 'patient',
    pageTemplate: '/patients/:patientId',
    featureArea: 'patient_workspace',
    screenType: 'workspace',
  },
  {
    segment: 'patients',
    pageTemplate: '/patients',
    featureArea: 'patient_management',
    screenType: 'list',
  },
  {
    segment: 'clinicians',
    pageTemplate: '/clinicians',
    featureArea: 'clinician_management',
    screenType: 'list',
  },
  {
    segment: 'templates',
    pageTemplate: '/templates',
    featureArea: 'templates',
    screenType: 'list',
  },
  {
    segment: 'activities',
    pageTemplate: '/activities',
    featureArea: 'custom_activities',
    screenType: 'list',
  },
  { segment: 'emr', pageTemplate: '/emr', featureArea: 'emr', screenType: 'integration' },
  {
    segment: 'extension',
    pageTemplate: '/extension',
    featureArea: 'extension',
    screenType: 'integration',
  },
  {
    segment: 'settings',
    pageTemplate: '/settings',
    featureArea: 'settings',
    screenType: 'settings',
  },
  {
    segment: 'privacy',
    pageTemplate: '/privacy',
    featureArea: 'privacy',
    screenType: 'public',
  },
];

const ROUTE_BY_SEGMENT: ReadonlyMap<string, RouteEntry> = new Map(
  ROUTE_TABLE.map((entry) => [entry.segment, entry]),
);

const UNKNOWN_ROUTE: NormalizedRoute = {
  pageTemplate: '/unknown',
  featureArea: 'unknown',
  screenType: 'unknown',
};

/** Extract the first path segment, ignoring query/hash and case. */
export function firstSegment(pathname: string): string {
  const pathOnly = (pathname.split('?')[0] ?? '').split('#')[0] ?? '';
  const trimmed = pathOnly.replace(/^\/+/, '').replace(/\/+$/, '');
  if (trimmed === '') {
    return '';
  }
  return (trimmed.split('/')[0] ?? '').toLowerCase();
}

/**
 * Map any location pathname to a PHI-free normalized route. Unknown paths
 * collapse to `/unknown` rather than echoing a path that could one day carry
 * an identifier.
 */
export function normalizeRoute(pathname: string | null | undefined): NormalizedRoute {
  if (!pathname) {
    return UNKNOWN_ROUTE;
  }
  const entry = ROUTE_BY_SEGMENT.get(firstSegment(pathname));
  if (!entry) {
    return UNKNOWN_ROUTE;
  }
  return {
    pageTemplate: entry.pageTemplate,
    featureArea: entry.featureArea,
    screenType: entry.screenType,
  };
}
